import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import { useUpdateOffer } from '~/hooks/apis/offers/useGetOffer';

import { ALLOWED_IMAGE_UPLOAD_ACCEPT } from '~/services/ImageService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import ContentActionBar, { CONTENT_ACTION_BUTTON_SX } from '../ContentActionBar';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog';
import OfferPagePreview from '../OfferPagePreview';
import SelectionDeleteBar from '../SelectionDeleteBar';
import useBulkImageSelection from '../useBulkImageSelection';
import type { OfferBuilderMediaController, SyncFailureReason } from '../useOfferBuilderMedia';

import ImageEditor from './ImageEditor';
import ImagesByRoom from './ImagesByRoom';
import LogoVideoSection from './LogoVideoSection';
import OfferGallery from './OfferGallery';
import OversizedImageEditor from './OversizedImageEditor';
import type { PropertyRoomTypeWithImages } from './types';
import { setFolderInputRef } from './utils';

const SYNC_FAILURE_REASON_LABEL: Record<SyncFailureReason, string> = {
  property_not_mapped: 'Not mapped to an accommodation property',
  room_not_mapped: 'Not mapped to an accommodation room',
  partial_import_failure: 'Some images failed to import',
  other: 'Sync call failed',
};

function describeSyncFailure(reason?: SyncFailureReason, errorMessage?: string) {
  if (reason === 'other' && errorMessage) {
    return `Sync call failed: ${errorMessage}`;
  }

  return SYNC_FAILURE_REASON_LABEL[reason ?? 'other'];
}

interface SyncAllImagesRoomResult {
  name: string;
  addedCount?: number;
  failureReason?: SyncFailureReason;
  errorMessage?: string;
}

interface SyncAllImagesSummary {
  gallery: SyncAllImagesRoomResult;
  rooms: Array<SyncAllImagesRoomResult>;
}

interface Props {
  media: OfferBuilderMediaController;
  offerId: string;
  offerSlug?: string;
  offerType?: string;
  propertyId?: string;
  propertyLogoId?: string;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
}

export default function ImagesVideo({
  media,
  offerId,
  offerSlug,
  offerType,
  propertyId,
  propertyLogoId,
  propertyRoomTypes,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const updateOfferMutation = useUpdateOffer(offerId);
  const [isSyncingAllImages, setIsSyncingAllImages] = useState(false);
  const [syncAllImagesSummary, setSyncAllImagesSummary] = useState<SyncAllImagesSummary>();
  const displayRoomImageOrderOverrides = useMemo(
    () => ({ ...media.savedRoomImageOrders, ...media.roomImageOrderOverrides }),
    [media.roomImageOrderOverrides, media.savedRoomImageOrders],
  );
  const {
    bulkDeleteDialogBody,
    isBulkDeleteDialogOpen,
    isBulkDeleting,
    selectedImagePublicIds,
    clearSelection,
    closeBulkDeleteDialog,
    confirmBulkDelete,
    openBulkDeleteDialog,
    toggleSelected,
  } = useBulkImageSelection({ deleteScopeLabel: 'the offer', media, selectableImages: media.mediaImages });

  const handleFolderInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';
    void media.uploadFiles(files, { ignoreNonImageFiles: true });
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';
    void media.uploadFiles(files);
  };

  const handleSyncAllImages = async () => {
    setIsSyncingAllImages(true);
    try {
      const { sections } = await media.syncAllImages(propertyId);
      const [galleryResult, ...roomResults] = sections;
      // Use each section's own label; propertyRoomTypes can drift out of sync by the time this resolves.
      const rooms = roomResults.map((room) => ({
        name: room.label,
        addedCount: room.failed ? undefined : room.syncedCount,
        failureReason: room.failed ? room.failureReason : undefined,
        errorMessage: room.failed ? room.errorMessage : undefined,
      }));

      setSyncAllImagesSummary({
        gallery: {
          name: 'Offer gallery',
          addedCount: galleryResult?.failed ? undefined : (galleryResult?.syncedCount ?? 0),
          failureReason: galleryResult?.failed ? galleryResult.failureReason : undefined,
          errorMessage: galleryResult?.failed ? galleryResult.errorMessage : undefined,
        },
        rooms,
      });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to sync images'), { variant: 'error' });
    } finally {
      setIsSyncingAllImages(false);
    }
  };

  const handleSaveVideo = async (videoId: string) => {
    try {
      await updateOfferMutation.mutateAsync({
        ...(offerType ? { type: offerType } : {}),
        video_vimeo_id: videoId || null,
      });
      media.setVideoId(videoId);
      enqueueSnackbar('Offer video updated', { variant: 'success' });
    } catch (error) {
      if (error instanceof Error) {
        reportError(error);
      }
      enqueueSnackbar(getErrorMessage(error, 'Failed to update offer video'), { variant: 'error' });
      throw error;
    }
  };

  return (
    <Box>
      <input
        hidden
        accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
        multiple
        type="file"
        id="offer-builder-folder-upload-input"
        ref={setFolderInputRef}
        onChange={handleFolderInputChange}
      />
      <input
        hidden
        accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
        multiple
        type="file"
        id="offer-builder-image-upload-input"
        onChange={handleFileInputChange}
      />

      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h4">Images & Video</Typography>
        <Typography variant="body1" color="text.secondary">
          Manage offer media from one place. Upload property images, organise them by room type, choose the property
          logo, and attach video content.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Name folders or files after a room to add images to that room, for example{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            Deluxe Suite/hero.jpg
          </Box>{' '}
          or{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            Deluxe Suite balcony.jpg
          </Box>
          . Anything without a room match is added to the offer gallery.
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ mb: 4 }}>
        <Stack direction="row" spacing={1.5}>
          <Button
            component="label"
            variant="outlined"
            htmlFor="offer-builder-folder-upload-input"
            disabled={media.isUploadingImages}
            startIcon={media.isUploadingImages ? <CircularProgress size={18} /> : <DriveFolderUploadOutlinedIcon />}
          >
            Upload folder
          </Button>
          <Button
            component="label"
            variant="contained"
            htmlFor="offer-builder-image-upload-input"
            disabled={media.isUploadingImages}
            startIcon={
              media.isUploadingImages ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <AddPhotoAlternateOutlinedIcon />
              )
            }
          >
            Add image
          </Button>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          <Button
            variant="text"
            disabled={isSyncingAllImages}
            startIcon={isSyncingAllImages ? <CircularProgress size={18} /> : <SyncOutlinedIcon />}
            onClick={handleSyncAllImages}
          >
            Sync all images
          </Button>
        </Stack>
        <OfferPagePreview offerId={offerId} offerSlug={offerSlug} />
      </Stack>

      <OfferGallery
        hiddenImagePublicIds={media.hiddenImagePublicIds}
        mediaImages={media.mediaImages}
        imageEdits={media.imageEdits}
        imagePublicIdReplacements={media.imagePublicIdReplacements}
        imageOrderOverrides={media.offerImageOrderOverrides}
        savedImageOrder={media.savedOfferGalleryOrder}
        isUploadDisabled={media.isUploadingImages}
        selectedImagePublicIds={selectedImagePublicIds}
        propertyId={propertyId}
        onDeleteImage={media.deleteImage}
        onReorderImages={media.reorderOfferGalleryImages}
        onSaveImageEdit={media.saveImageEdit}
        onToggleHiddenImage={media.toggleHiddenImage}
        onToggleSelected={toggleSelected}
        onUploadImages={media.uploadFilesToOfferGallery}
        onImportImages={media.importImages}
        onSyncGalleryImages={media.syncGalleryImages}
      />

      <ImagesByRoom
        hiddenImagePublicIds={media.hiddenImagePublicIds}
        imagePublicIdReplacements={media.imagePublicIdReplacements}
        mediaImages={media.mediaImages}
        selectedImagePublicIds={selectedImagePublicIds}
        onEditImage={media.editRoomImage}
        onImportRoomImages={media.importRoomImages}
        onSyncTopRoomImages={media.syncTopRoomImages}
        onRemoveImageFromRoom={media.removeImageFromRoom}
        onReorderRoomImages={media.reorderRoomImages}
        onToggleHiddenImage={media.toggleHiddenImage}
        onToggleSelected={toggleSelected}
        onUploadImagesToRoom={media.uploadFilesToRoom}
        propertyRoomTypes={propertyRoomTypes}
        removedRoomImageIdsByRoomKey={media.removedRoomImageIdsByRoomKey}
        roomImageOrderOverrides={displayRoomImageOrderOverrides}
        roomImageOrderResetKey={media.roomImageOrderResetKey}
        uploadedRoomAssignments={media.uploadedRoomAssignments}
      />
      <LogoVideoSection
        isSavingVideo={updateOfferMutation.isPending}
        propertyId={propertyId}
        propertyLogoId={propertyLogoId}
        videoId={media.videoId}
        onSaveVideo={handleSaveVideo}
      />
      <ImageEditor
        edit={media.roomImagePendingEdit ? media.imageEdits[media.roomImagePendingEdit.publicId] : undefined}
        image={media.roomImagePendingEdit}
        open={!!media.roomImagePendingEdit}
        onClose={() => media.setRoomImagePendingEdit(undefined)}
        onDeleteImage={media.removeRoomImageFromEditor}
        onSave={media.saveImageEdit}
      />
      <OversizedImageEditor
        file={media.pendingOversizedUpload?.file}
        onCancel={media.cancelOversizedUpload}
        onSave={media.confirmOversizedUpload}
      />
      <DeleteConfirmationDialog
        body="This will remove the image from the room. This action cannot be undone."
        open={!!media.roomImagePendingDeletion}
        title="Delete image from room?"
        onClose={media.closeRoomImageDeleteDialog}
        onConfirm={media.confirmRemoveRoomImage}
      />
      <DeleteConfirmationDialog
        body={bulkDeleteDialogBody}
        isConfirming={isBulkDeleting}
        open={isBulkDeleteDialogOpen}
        title="Delete selected images?"
        onClose={closeBulkDeleteDialog}
        onConfirm={confirmBulkDelete}
      />
      <SelectionDeleteBar
        selectedCount={selectedImagePublicIds.length}
        onClear={clearSelection}
        onDeleteSelected={openBulkDeleteDialog}
      />
      <Dialog open={!!syncAllImagesSummary} onClose={() => setSyncAllImagesSummary(undefined)} maxWidth="xs" fullWidth>
        <DialogTitle>Sync all images</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <List disablePadding>
            {syncAllImagesSummary && (
              <ListItem disableGutters divider>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {syncAllImagesSummary.gallery.addedCount !== undefined ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={syncAllImagesSummary.gallery.name}
                  secondary={
                    syncAllImagesSummary.gallery.addedCount !== undefined
                      ? `${syncAllImagesSummary.gallery.addedCount} ${
                          syncAllImagesSummary.gallery.addedCount === 1 ? 'image' : 'images'
                        } added`
                      : describeSyncFailure(
                          syncAllImagesSummary.gallery.failureReason,
                          syncAllImagesSummary.gallery.errorMessage,
                        )
                  }
                  slotProps={{
                    secondary: syncAllImagesSummary.gallery.addedCount === undefined ? { color: 'error' } : undefined,
                  }}
                />
              </ListItem>
            )}
            {syncAllImagesSummary?.rooms.map(({ name, addedCount, failureReason, errorMessage }, index) => {
              const isLast = index === syncAllImagesSummary.rooms.length - 1;

              return (
                <ListItem key={`${name}-${index}`} disableGutters divider={!isLast}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {addedCount !== undefined ? (
                      <CheckCircleOutlineIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorOutlineIcon color="error" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={name}
                    secondary={
                      addedCount !== undefined
                        ? `${addedCount} ${addedCount === 1 ? 'image' : 'images'} added`
                        : describeSyncFailure(failureReason, errorMessage)
                    }
                    slotProps={{
                      secondary: addedCount === undefined ? { color: 'error' } : undefined,
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncAllImagesSummary(undefined)}>Close</Button>
        </DialogActions>
      </Dialog>
      <ContentActionBar
        hasUnsavedChanges={media.hasUnsavedOfferChanges || media.hasUnsavedRoomImageChanges}
        isSaving={media.isSavingChanges || media.isUploadingImages}
        tabName="Images & Video"
        onDiscard={media.discardChanges}
      >
        <Button
          variant="contained"
          disabled={media.isSavingChanges || media.isUploadingImages}
          onClick={() => void media.saveImagesVideoChanges()}
          sx={CONTENT_ACTION_BUTTON_SX}
        >
          {media.isSavingChanges ? 'Saving...' : 'Save changes'}
        </Button>
      </ContentActionBar>
    </Box>
  );
}
